import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '../shared/components/BrandLogo';
import { Icon } from '../shared/components/Icon';
import { useInstall } from './install';

/**
 * The invitation to install, and two distinct ways to decline it.
 *
 * **Not now** clears it for this browser session: a reload does not bring it back, and the next
 * time Varza is opened it may be welcome. **Don't show this again** is a decision, kept on the
 * device until it is reversed in Settings.
 *
 * Neither answer takes installing away. Settings > This device offers it whenever the operator
 * wants it, which is what makes it fair to stop asking.
 */
export function InstallBanner() {
  const { t } = useTranslation();
  const {
    dismissForSession,
    dismissedForSession,
    install,
    installed,
    reminderSuppressed,
    route,
    suppressReminder,
  } = useInstall();
  const [showSteps, setShowSteps] = useState(false);

  if (installed || route === 'none' || reminderSuppressed || dismissedForSession) return null;

  // Safari has no installation prompt to open, so the invitation carries the two steps its own
  // menu needs instead of a button that could not do anything.
  const manualStepsKey = route === 'ios-share' ? 'pwa.install.iosSteps' : 'pwa.install.safariSteps';

  return (
    <aside className="install-banner" role="complementary" aria-label={t('pwa.install.title')}>
      <span className="install-banner__mark" aria-hidden="true"><BrandLogo /></span>

      <div className="install-banner__copy">
        <span className="brand-tagline">{t('app.tagline')}</span>
        <strong>{t('pwa.install.title')}</strong>
        <span>{t('pwa.install.subtitle')}</span>
        {showSteps && <span className="install-banner__steps">{t(manualStepsKey)}</span>}
        <button className="text-link install-banner__never" type="button" onClick={suppressReminder}>
          {t('pwa.install.never')}
        </button>
      </div>

      <div className="install-banner__actions">
        {route === 'prompt' ? (
          <button className="button button--primary" type="button" onClick={() => void install()}>
            <Icon name="install" />
            {t('pwa.install.action')}
          </button>
        ) : (
          <button className="button button--secondary" type="button" onClick={() => setShowSteps((value) => !value)} aria-expanded={showSteps}>
            <Icon name="install" />
            {t('pwa.install.how')}
          </button>
        )}
        <button className="icon-button" type="button" onClick={dismissForSession} aria-label={t('pwa.install.dismiss')} title={t('pwa.install.dismiss')}>
          <Icon name="close" />
        </button>
      </div>
    </aside>
  );
}
